/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {OverlayPanel} from 'primereact/overlaypanel';
import {Button} from 'primereact/button';
import ButtonIcon from '../controls/ButtonIcon';
import Form from '../forms/Form';
import FormBody from '../forms/FormBody';
import Field from '../forms/Field';
import useSessionState from '../../hooks/useSessionState';
import IconLibrary from "../iconLibrary";

// ── Slot components — used only to carry children; FiltroV2 reads them by displayName
export const LeftFilters = ({children}) => children ?? null;
export const RightFilters = ({children}) => children ?? null;
export const ModalFilters = ({children}) => children ?? null;

LeftFilters.displayName = 'LeftFilters';
RightFilters.displayName = 'RightFilters';
ModalFilters.displayName = 'ModalFilters';

// count modal Field children that have a non-empty value in the current filter
// fields starting with "_" are considered internal and excluded from the count
function countActiveModalFilters(modalChildren, filterState) {
    let count = 0;
    React.Children.forEach(modalChildren, (child) => {
        if (!child || child.type !== Field) return;
        const fieldName = child.props.field;
        if (!fieldName || fieldName.startsWith('_')) return;
        const val = filterState[fieldName];
        if (val === null || val === undefined || val === '') return;
        if (Array.isArray(val) && val.length === 0) return;
        count++;
    });
    return count;
}

function collectSlots(children) {
    const slots = {left: null, right: null, modal: null};
    React.Children.forEach(children, (child) => {
        if (!child) return;
        const name = child.type?.displayName;
        if (name === 'LeftFilters') slots.left = child.props.children;
        else if (name === 'RightFilters') slots.right = child.props.children;
        else if (name === 'ModalFilters') slots.modal = child.props.children;
    });
    return slots;
}

/**
 * FiltroV2
 *
 * Props:
 *   filtroKey        {string}   — sessionStorage key for persisting filter
 *   defaultFilter    {object}   — initial values
 *   onApplyFilter    {fn}       — called with filterState when user searches / applies
 *   filtrarAlInicio  {bool}     — auto-search on mount (default true)
 *   modalTitle       {string}   — title for the modal header
 *   modalColumns     {number}   — grid columns inside modal Form (default 2)
 *
 * Ref handle:
 *   getFilter()                 — returns current filterState
 *   setFilter(patch, options)   — merges patch into filterState;
 *                                 options.submit=true also calls onApplyFilter
 *
 * Children:
 *   <LeftFilters>   — Field components rendered inline on the left
 *   <ModalFilters>  — Field components rendered inside the modal dialog
 *   <RightFilters>  — Field components rendered inline on the right
 */
const FiltroV2 = forwardRef(function FiltroV2(
    {
        filtroKey,
        defaultFilter = {},
        onApplyFilter,
        filtrarAlInicio = true,
        modalTitle = 'Filtros adicionales',
        modalColumns = 2,
        children,
    },
    ref
) {
    const storageKey = filtroKey ? `filtroV2_${filtroKey}` : 'filtroV2_noop';
    const [sessionFilter, setSessionFilter] = useSessionState(storageKey, defaultFilter, Boolean(filtroKey));
    const [localFilter, setLocalFilter] = useState(defaultFilter);
    const filterState = filtroKey ? sessionFilter : localFilter;
    const setFilterState = filtroKey ? setSessionFilter : setLocalFilter;

    const [modalDraft, setModalDraft] = useState({});
    const overlayRef = useRef(null);
    const filterBtnRef = useRef(null);

    const slots = useMemo(() => collectSlots(children), [children]);

    const activeModalCount = useMemo(
        () => countActiveModalFilters(slots.modal, filterState),
        [slots.modal, filterState]
    );

    const didMount = useRef(false);
    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            if (filtrarAlInicio && onApplyFilter) onApplyFilter(filterState);
        }
    }, []);

    useImperativeHandle(ref, () => ({
        getFilter: () => filterState,
        setFilter: (patch, options = {}) => {
            const next = {...filterState, ...patch};
            setFilterState(next);
            if (options.submit && onApplyFilter) onApplyFilter(next);
        },
    }), [filterState, setFilterState, onApplyFilter]);

    // onChange handler for inline left/right Form — receives full updated values
    const handleInlineChange = (newValues) => {
        setFilterState(newValues);
    };

    const openModal = (e) => {
        setModalDraft({...filterState});
        overlayRef.current?.toggle(e);
    };

    const closeOverlay = () => {
        overlayRef.current?.hide();
    };

    const applyModal = () => {
        const merged = {...filterState, ...modalDraft};
        setFilterState(merged);
        closeOverlay();
        if (onApplyFilter) onApplyFilter(merged);
    };

    const resetModal = () => {
        const modalFieldNames = React.Children.toArray(slots.modal)
            .filter((c) => c && c.props?.field)
            .map((c) => c.props.field);

        const patch = {};
        modalFieldNames.forEach((f) => {
            patch[f] = defaultFilter[f] ?? null;
        });

        const next = {...filterState, ...patch};

        Object.keys(next).forEach(key => {
            if (next[key] === null) {
                delete next[key];
            }
        });

        setFilterState(next);
        closeOverlay();
        if (onApplyFilter) onApplyFilter(next);
    };

    const handleSearch = () => {
        if (onApplyFilter) onApplyFilter(filterState);
    };

    const handleReset = () => {
        const reset = {...defaultFilter};
        setFilterState(reset);
        if (onApplyFilter) onApplyFilter(reset);
    };

    // renders Field children from a slot through FormBody (sharing filterState)
    const renderFields = (slotChildren, values, onChange, flexDirection) => {
        if (!slotChildren) return null;
        const fields = React.Children.toArray(slotChildren);
        return (
            <FormBody
                values={values}
                onChange={onChange}
                fields={fields}
                flexDirection={flexDirection}
            />
        );
    };

    return (
        <>
            <form
                style={{display: 'flex', alignItems: 'center', gap: '8px', flexGrow: 1}}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch();
                }}
            >
                {/* ── inline filter area ── */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flex: 1,
                    gap: '8px',
                    flexWrap: 'wrap'
                }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        {slots.left && (
                            <div id={'inline-filter-area'} style={{display: 'flex', gap: '8px'}}>
                                {renderFields(slots.left, filterState, handleInlineChange, 'row')}
                            </div>
                        )}
                        <button ref={filterBtnRef} type="button" className="button-icon position-relative FcFilledFilter"
                                title={`Mostrar Filtros`}
                                onClick={openModal}>
                            <IconLibrary iconName="FcFilledFilter"/>
                            <i className={`pi pi-plus-circle`}
                               style={{position: "absolute", fontSize: "10px", right: "3px", bottom: "3px"}}/>
                            {activeModalCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-4px',
                                        right: '-4px',
                                        background: '#e53935',
                                        color: '#fff',
                                        borderRadius: '50%',
                                        width: '16px',
                                        height: '16px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: 'none',
                                        lineHeight: 1,
                                    }}
                                >
                                {activeModalCount}
                            </span>
                            )}
                        </button>

                    </div>


                    {slots.right && (
                        <div className="filtro-v2-right" style={{marginLeft: 'auto'}}>
                            {renderFields(slots.right, filterState, handleInlineChange, 'row')}
                        </div>
                    )}
                    <ButtonIcon title="Borrar Filtros" onClick={handleReset} iconName="FcClearFilters"/>

                    <ButtonIcon
                        title="Buscar"
                        onClick={handleSearch}
                        className="action"
                        iconName="BsSearch"
                    />
                </div>
            </form>

            {/* ── overlay panel anclado al botón de filtros ── */}
            <OverlayPanel ref={overlayRef} style={{width: '560px'}} dismissable>
                <div style={{padding: '4px 0'}}>
                    <div style={{fontWeight: 600, marginBottom: '12px', fontSize: '14px'}}>
                        {modalTitle}
                    </div>
                    <Form
                        values={modalDraft}
                        onChange={setModalDraft}
                        columns={modalColumns}
                        className="form-container"
                    >
                        {slots.modal}
                    </Form>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '16px',
                        borderTop: '1px solid #e0e0e0',
                        paddingTop: '12px',
                    }}>
                        <Button
                            label="Restablecer filtros"
                            className="p-button-text p-button-sm p-button-secondary"
                            icon="pi pi-refresh"
                            onClick={resetModal}
                        />
                        <div style={{display: 'flex', gap: '8px'}}>
                            <Button
                                label="Cancelar"
                                className="p-button-text p-button-sm"
                                onClick={closeOverlay}
                            />
                            <Button label="Aplicar" className="p-button-sm" onClick={applyModal} autoFocus/>
                        </div>
                    </div>
                </div>
            </OverlayPanel>
        </>
    );
});

export default FiltroV2;
